import { Checkbox, Calendar, Divider, IconTextBtn, Avatar } from '@sketchnote/shared-components';
import update from 'immutability-helper';
import * as React from 'react';
import { useReadOnly } from 'slate-react';
import { PortalBody, ELEMENT_DEFAULT } from '@udecode/plate';
import { parseISO, formatISO, format } from 'date-fns';
import castArray from 'lodash/castArray';
import { usePopper } from 'react-popper';
import cn from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import DataGrid, { Row, ValueFormatter } from '@sketchnote/data-table';
import { EmailTwoIcon, TextIcon, CheckboxIcon, PersonIcon, MultiSelectIcon, SelectIcon, DateIcon, NumberIcon, EmailIcon, LinkTwoIcon, ChevronDownIcon, SortAsc, SortDesc, SortIcon, PlusIcon, InsertLeft, InsertRight, TrashIcon, AddIcon, MoreIcon, ArrowUpTwoIcon, ArrowDownTwoIcon, ClipboardIcon, DeleteIcon, LinkIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import isHotkey from 'is-hotkey';
import { useMultipleSelection, useCombobox } from 'downshift';
import { v4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { Node } from 'slate';
import { Popover, PopoverTrigger, Portal, PopoverContent } from '@chakra-ui/react';
import TextareaAutosize from 'react-textarea-autosize';
const {
  useState: useState$7,
  useEffect: useEffect$6,
  useCallback: useCallback$7
} = React;

const CheckboxRenderer = ({
  column,
  row,
  onRowChange
}) => {
  var _a;

  const [isChecked, setIsChecked] = useState$7(false);
  const readOnly = useReadOnly();
  useEffect$6(() => {
    var _a;

    setIsChecked(!!((_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]));
  }, [(_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]]);
  const handleClick = useCallback$7(() => {
    var _a;

    if (!readOnly) {
      setIsChecked(!isChecked);
      const updatedRow = update(row, {
        $merge: {
          [column.key]: {
            [column.type]: !((_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type])
          }
        }
      });
      onRowChange(updatedRow);
    }
  }, [readOnly, isChecked, row, column]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Checkbox, {
    color: "#2BC545",
    onChange: handleClick,
    checked: isChecked,
    variant: "white"
  }));
};

const {
  useRef: useRef$4,
  useMemo: useMemo$5,
  useEffect: useEffect$5
} = React;

const DateEditor = ({
  column,
  row,
  onRowChange
}) => {
  var _a, _b;

  const referenceElement = useRef$4(null);
  const popperElement = useRef$4(null);
  const {
    styles,
    attributes
  } = usePopper(referenceElement.current, popperElement.current, {
    placement: 'bottom-start'
  });
  const dates = useMemo$5(() => {
    var _a, _b, _c, _d;

    const d = ((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) ? castArray((_d = (_c = row === null || row === void 0 ? void 0 : row[column.key]) === null || _c === void 0 ? void 0 : _c[column.type]) === null || _d === void 0 ? void 0 : _d.value) : [];
    return d.map(item => parseISO(item));
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value]); // The popper postioing only works after 2nd rerender
  // below code is to position the date component
  // correctly

  useEffect$5(() => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            value: dates.map(date => formatISO(date))
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, []);

  const handleChangeDate = dates => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            value: dates.map(date => formatISO(date))
          }
        }
      }
    });
    onRowChange(updatedRow, true);
  };

  return /*#__PURE__*/React.createElement("div", {
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement("button", {
    ref: referenceElement
  }, dates.map(date => format(date, 'do MMM yyyy')).join(' → ')), /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: popperElement,
    style: styles.popper
  }, attributes.popper), /*#__PURE__*/React.createElement(Calendar, {
    onChange: handleChangeDate,
    value: dates.length > 0 ? dates : null
  }))));
};

const DateFormatter = ({
  column,
  row
}) => {
  var _a, _b;

  const dates = useMemo$5(() => {
    var _a, _b, _c, _d;

    const d = ((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) ? castArray((_d = (_c = row === null || row === void 0 ? void 0 : row[column.key]) === null || _c === void 0 ? void 0 : _c[column.type]) === null || _d === void 0 ? void 0 : _d.value) : [];
    return d.map(item => parseISO(item));
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value]);
  return /*#__PURE__*/React.createElement("div", null, dates.map(date => format(date, 'do MMM yyyy')).join(' → '));
};
/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */


function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const {
  useCallback: useCallback$6
} = React;

function DraggableRowRenderer(_a) {
  var {
    rowIdx,
    isRowSelected,
    className,
    onRowReorder
  } = _a,
      props = __rest(_a, ["rowIdx", "isRowSelected", "className", "onRowReorder"]);

  const readOnly = useReadOnly();
  const [{
    isDragging
  }, drag] = useDrag({
    type: 'ROW_DRAG',
    canDrag: !readOnly,
    item: {
      index: rowIdx
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  const [{
    isOver
  }, drop] = useDrop({
    accept: 'ROW_DRAG',

    drop({
      index
    }) {
      onRowReorder(index, rowIdx);
    },

    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });
  className = cn(className, {
    'opacity-50': isDragging,
    'bg-gray-200 border-b-2 border-primary-500': isOver
  });
  return /*#__PURE__*/React.createElement(Row // ref={useCombinedRefs(drag, drop)}
  , Object.assign({
    // ref={useCombinedRefs(drag, drop)}
    rowIdx: rowIdx,
    isRowSelected: isRowSelected,
    className: className
  }, props));
}

function useCombinedRefs$1(...refs) {
  return useCallback$6(handle => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(handle);
      } else if (ref !== null) {
        // @ts-expect-error: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31065
        ref.current = handle;
      }
    }
  }, refs);
}

function DraggableHeaderWrapper({
  onColumnsReorder,
  column,
  children
}) {
  const readOnly = useReadOnly();
  const [{
    isDragging
  }, drag] = useDrag({
    type: 'COLUMN_DRAG',
    item: {
      key: column.key
    },
    canDrag: !readOnly,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  const [{
    isOver
  }, drop] = useDrop({
    accept: 'COLUMN_DRAG',

    drop({
      key
    }) {
      onColumnsReorder(key, column.key);
    },

    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: useCombinedRefs$1(drag, drop),
    className: cn('h-full pl-12 pt-14', {
      'opacity-50': isDragging,
      'bg-gray-200': isOver
    }),
    style: {
      cursor: isDragging ? 'grabbing' : 'grab'
    }
  }, children);
}

const {
  useCallback: useCallback$5
} = React;

const EmailEditor = ({
  column,
  row,
  onRowChange,
  onClose
}) => {
  var _a, _b;

  const updateAndClose = useCallback$5(() => {
    onClose(true);
  }, [onClose]);
  const handleOnChange = useCallback$5(e => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            value: e.target.value
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, [onRowChange, row, column]);
  const handleOnKeyDown = useCallback$5(e => {
    if (isHotkey('esc', e) || isHotkey('enter', e)) {
      updateAndClose();
    }
  }, [updateAndClose]);
  return /*#__PURE__*/React.createElement("div", {
    className: "w-full"
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value,
    className: "w-full bg-inherit",
    onChange: handleOnChange,
    autoFocus: true,
    onKeyDown: handleOnKeyDown,
    onBlur: updateAndClose
  }));
};

const EmailFormatter = ({
  column,
  row
}) => {
  var _a, _b, _c, _d, _e, _f, _g;

  return /*#__PURE__*/React.createElement("div", {
    className: "w-full h-full flex justify-between relative table-url-formatter"
  }, /*#__PURE__*/React.createElement("span", {
    className: "break-all"
  }, (_c = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : ''), ((_e = (_d = row === null || row === void 0 ? void 0 : row[column.key]) === null || _d === void 0 ? void 0 : _d[column.type]) === null || _e === void 0 ? void 0 : _e.value) && /*#__PURE__*/React.createElement(Tippy, {
    content: "Send email",
    className: "w-auto h-auto bg-gray-999"
  }, /*#__PURE__*/React.createElement("a", {
    href: `mailto:${(_g = (_f = row === null || row === void 0 ? void 0 : row[column.key]) === null || _f === void 0 ? void 0 : _f[column.type]) === null || _g === void 0 ? void 0 : _g.value}`,
    className: "p-6 bg-white underline shadow-sm text-lg hidden rounded-md text-gray-400 absolute top-2 right-2 table-url-format-icon"
  }, /*#__PURE__*/React.createElement(EmailTwoIcon, null))));
};

const TablePlusColors = {
  Blue: 'bg-blue-100',
  Green: 'bg-green-100',
  Red: 'bg-red-100',
  Yellow: 'bg-yellow-100',
  Pink: 'bg-pink-100',
  Purple: 'bg-purple-100',
  Orange: 'bg-orange-100'
};
const TablePlusBorderColors = {
  Blue: 'border-blue-500',
  Green: 'border-green-500',
  Red: 'border-red-500',
  Yellow: 'border-yellow-500',
  Pink: 'border-pink-500',
  Purple: 'border-purple-500',
  Orange: 'border-orange-500'
};
const TablePlusColorsKeys = Object.keys(TablePlusColors);
const propertyOptions = [{
  label: 'Text',
  value: 'text'
}, {
  label: 'Date',
  value: 'date'
}, {
  label: 'Checkbox',
  value: 'checkbox'
}, {
  label: 'Person',
  value: 'person'
}, {
  label: 'Multi Select',
  value: 'multi-tag'
}, {
  label: 'Single Tag',
  value: 'single-tag'
}, {
  label: 'Number',
  value: 'number'
}, {
  label: 'Email',
  value: 'email'
}, {
  label: 'Url',
  value: 'url'
}];
const propertyLabel = {
  text: 'Text',
  select: 'Select',
  checkbox: 'Checkbox',
  person: 'Person',
  'multi-tag': 'Multi Select',
  'single-tag': 'Single Tag',
  date: 'Date',
  number: 'Number',
  email: 'Email',
  url: 'Url'
};
const propertyIcon = {
  text: TextIcon,
  checkbox: CheckboxIcon,
  person: PersonIcon,
  'multi-tag': MultiSelectIcon,
  'single-tag': SelectIcon,
  date: DateIcon,
  number: NumberIcon,
  email: EmailIcon,
  url: LinkTwoIcon
};
const {
  useState: useState$6,
  forwardRef,
  useEffect: useEffect$4,
  Fragment: Fragment$1,
  useMemo: useMemo$4
} = React;

const HeaderMenu = _a => {
  var {
    column,
    onAddColumn,
    onDeleteColumn,
    onNameChange,
    onChangeColor,
    onPropertyChange,
    onSortChange,
    sortColumns,
    isReadOnly
  } = _a,
      props = __rest(_a, ["column", "onAddColumn", "onDeleteColumn", "onNameChange", "onChangeColor", "onPropertyChange", "onSortChange", "sortColumns", "isReadOnly"]);

  const [isOpen, setIsOpen] = useState$6(false);
  const [columnName, setColumnName] = useState$6('');

  const handleOpenMenu = value => {
    if (value === false) {
      onNameChange(columnName);
    }

    setIsOpen(value);
  };

  const firstFieldRef = React.useRef(null);
  useEffect$4(() => {
    setColumnName(column.name);
  }, [column.name]);

  const handleOnColumnName = e => {
    setColumnName(e.target.value);
  };

  const handleSortDesc = () => {
    onSortChange('DESC');
  };

  const handleSortAsc = () => {
    onSortChange('ASC');
  };

  const handleSortClear = () => {
    onSortChange('');
  };

  const handleAddColRight = () => {
    onAddColumn('right');
  };

  const handleAddColLeft = () => {
    onAddColumn('left');
  };

  const sortedInfo = useMemo$4(() => {
    const foundCol = sortColumns.find(obj => obj.columnKey === column.key);

    if (foundCol) {
      return {
        isSorted: true,
        direction: foundCol.direction
      };
    } else {
      return {
        isSorted: false
      };
    }
  }, [sortColumns, column]);
  const PropertyIcon = (propertyIcon === null || propertyIcon === void 0 ? void 0 : propertyIcon[props === null || props === void 0 ? void 0 : props.type]) || React.Fragment;
  return /*#__PURE__*/React.createElement(Popover, {
    onOpen: handleOpenMenu.bind(undefined, true),
    closeOnBlur: true,
    isOpen: isOpen,
    onClose: handleOpenMenu.bind(undefined, false),
    placement: "bottom",
    initialFocusRef: firstFieldRef
  }, ({
    isOpen
  }) => /*#__PURE__*/React.createElement(Fragment$1, null, /*#__PURE__*/React.createElement(PopoverTrigger, null, /*#__PURE__*/React.createElement("button", {
    className: "w-full text-left flex justify-between items-center table-header-menu-editor",
    style: {
      cursor: 'inherit'
    },
    disabled: isReadOnly
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex items-center"
  }, /*#__PURE__*/React.createElement("span", {
    className: cn({
      'px-8 py-1 rounded truncate': true,
      [TablePlusColors === null || TablePlusColors === void 0 ? void 0 : TablePlusColors[column.color]]: !!column.color
    })
  }, columnName), sortedInfo.isSorted ? sortedInfo.direction === 'ASC' ? /*#__PURE__*/React.createElement(SortAsc, {
    className: "ml-10 text-gray-500"
  }) : sortedInfo.direction === 'DESC' ? /*#__PURE__*/React.createElement(SortDesc, {
    className: "ml-10 text-gray-500"
  }) : null : null), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center"
  }, /*#__PURE__*/React.createElement("span", {
    className: "mr-10 text-2xl text-gray-500 flex"
  }, /*#__PURE__*/React.createElement(PropertyIcon, null)), /*#__PURE__*/React.createElement(ChevronDownIcon, {
    className: "text-gray-400 mr-13 hidden table-header-arrow-icon cursor-pointer",
    wrapperClass: "flex"
  })))), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(PopoverContent // The maxheight property and overflow doesn't work properly on Safari
  // So, the below code is a small workaround for that
  , {
    // The maxheight property and overflow doesn't work properly on Safari
    // So, the below code is a small workaround for that
    style: {
      maxHeight: isSafari ? 'none' : undefined
    },
    className: "p-12 bg-white shadow-mdl3 rounded max-h-320 overflow-auto"
  }, !isReadOnly && isOpen ? /*#__PURE__*/React.createElement(Fragment$1, null, /*#__PURE__*/React.createElement("input", {
    className: "bg-gray-100 mb-13 border border-gray-200 rounded-md px-9 py-6",
    ref: firstFieldRef,
    onChange: handleOnColumnName,
    value: columnName,
    onKeyDown: e => {
      if (e.key === 'Enter') {
        handleOpenMenu(false);
      }
    }
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-black text-xs px-8 py-12"
  }, "Column type"), /*#__PURE__*/React.createElement(Popover, {
    trigger: "hover",
    placement: "right"
  }, /*#__PURE__*/React.createElement(PopoverTrigger, null, /*#__PURE__*/React.createElement(Item, {
    leftIcon: propertyIcon === null || propertyIcon === void 0 ? void 0 : propertyIcon[props.type],
    isRightArrow: true,
    label: propertyLabel === null || propertyLabel === void 0 ? void 0 : propertyLabel[props.type]
  })), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(PopoverContent, {
    className: "p-12 bg-white shadow-md rounded",
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  }, propertyOptions.map(obj => /*#__PURE__*/React.createElement(Item, {
    leftIcon: propertyIcon[obj.value],
    key: obj.value,
    label: propertyLabel[obj.value],
    onMouseDown: () => {
      onPropertyChange(obj.value);
    }
  }))))), /*#__PURE__*/React.createElement(Divider, {
    style: {
      marginBottom: '0.5rem',
      marginTop: '0.5rem'
    }
  }), /*#__PURE__*/React.createElement(Popover, {
    trigger: "hover",
    placement: "right"
  }, /*#__PURE__*/React.createElement(PopoverTrigger, null, /*#__PURE__*/React.createElement(Item // Safari Not rendering the SortIcon correctly
  , {
    // Safari Not rendering the SortIcon correctly
    leftIcon: isSafari ? SortAsc : SortIcon,
    isRightArrow: true,
    label: 'Sort column'
  })), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(PopoverContent, {
    className: "p-12 bg-white shadow-md rounded"
  }, /*#__PURE__*/React.createElement(Item, {
    leftIcon: SortAsc,
    label: "Sort ascending",
    onMouseDown: handleSortAsc
  }), /*#__PURE__*/React.createElement(Item, {
    leftIcon: SortDesc,
    label: "Sort descending",
    onMouseDown: handleSortDesc
  }), /*#__PURE__*/React.createElement(Item, {
    leftIcon: SortIcon,
    label: "Clear Sorting",
    onMouseDown: handleSortClear
  })))), /*#__PURE__*/React.createElement(Popover, {
    trigger: "hover",
    placement: "right"
  }, /*#__PURE__*/React.createElement(PopoverTrigger, null, /*#__PURE__*/React.createElement(Item, {
    leftIcon: PlusIcon,
    isRightArrow: true,
    label: 'Insert column'
  })), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(PopoverContent, {
    className: "p-12 bg-white shadow-md rounded",
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement(Item, {
    leftIcon: InsertLeft,
    onMouseDown: handleAddColLeft,
    label: "Insert left"
  }), /*#__PURE__*/React.createElement(Item, {
    leftIcon: InsertRight,
    onMouseDown: handleAddColRight,
    label: "Insert right"
  })))), /*#__PURE__*/React.createElement(Item, {
    leftIcon: TrashIcon,
    onClick: onDeleteColumn,
    label: "Delete"
  }), /*#__PURE__*/React.createElement(Divider, {
    style: {
      marginBottom: '0.5rem',
      marginTop: '0.5rem'
    }
  }), Object.keys(TablePlusColors).map(color => /*#__PURE__*/React.createElement("button", {
    className: "px-8 py-7 rounded-sm min-w-btn-lg hover:bg-gray-100 cursor-pointer align-middle text-left",
    key: TablePlusColors[color],
    onClick: onChangeColor.bind(undefined, color)
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-gray-500 mr-8 align-middle"
  }, /*#__PURE__*/React.createElement("span", {
    className: `border ${TablePlusColors[color]} ${TablePlusBorderColors[color]} mr-6 h-14 w-14 rounded-full inline-block`
  }), color)))) : null))));
};

const Item = forwardRef( // eslint-disable-next-line react/prop-types
(_a, ref) => {
  var {
    isRightArrow,
    label,
    leftIcon: LeftIcon
  } = _a,
      otherProps = __rest(_a, ["isRightArrow", "label", "leftIcon"]);

  return /*#__PURE__*/React.createElement("button", Object.assign({
    ref: ref,
    className: "px-8 w-full text-left py-7 rounded-sm min-w-btn-lg hover:bg-gray-100 cursor-pointer align-middle justify-between flex items-center font-regular"
  }, otherProps), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center"
  }, LeftIcon && /*#__PURE__*/React.createElement(LeftIcon, {
    className: "mr-8 text-lg text-gray-500"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-sm"
  }, label)), isRightArrow ? /*#__PURE__*/React.createElement("span", {
    style: {
      transform: 'rotate(-90deg)',
      transition: 'all 0.5s'
    }
  }, /*#__PURE__*/React.createElement(ChevronDownIcon, {
    className: "text-gray-200"
  })) : null);
});
Item.displayName = 'Item';
const isSafari = typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator === null || navigator === void 0 ? void 0 : navigator.userAgent);

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "/* Styles for custom table in editor also called react data grid */\n\n.table-module_sn-table__2qRK2 .rdt-text-editor {\n  -webkit-appearance: none;\n     -moz-appearance: none;\n          appearance: none;\n  box-sizing: border-box;\n  width: 100%;\n  height: 100%;\n  padding: 0px 6px 0 6px;\n  border: 2px solid #ccc;\n  vertical-align: top;\n  color: var(--color);\n  background-color: var(--background-color);\n  font-family: inherit;\n  font-size: var(--font-size);\n}\n\n.table-module_sn-table__2qRK2 .rdt-text-editor:focus {\n  border-color: var(--selection-color);\n  outline: none;\n}\n\n.table-module_sn-table__2qRK2 .rdt-text-editor::-moz-placeholder {\n  color: #999;\n  opacity: 1;\n}\n\n.table-module_sn-table__2qRK2 .rdt-text-editor:-ms-input-placeholder {\n  color: #999;\n  opacity: 1;\n}\n\n.table-module_sn-table__2qRK2 .rdt-text-editor::placeholder {\n  color: #999;\n  opacity: 1;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell {\n  padding: 5px 8px 6px;\n  border-right: 1px solid var(--border-color);\n  border-bottom: 1px solid var(--border-color);\n  overflow: hidden;\n  text-overflow: clip;\n  word-wrap: break-word;\n  position: relative;\n  background-color: inherit;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell[aria-selected='true'] {\n  box-shadow: inset 0 0 0 2px #254dda;\n  border-radius: 0.4rem;\n}\n\n.table-module_sn-table__2qRK2 .rdt-header-row .rdt-cell {\n  padding: 0;\n}\n\n.table-module_sn-table__2qRK2 .rdt {\n  --color: #000;\n  --border-color: #ddd;\n  --summary-border-color: #aaa;\n  --background-color: hsl(0deg 0% 100%);\n  --header-background-color: hsl(0deg 0% 97.5%);\n  --row-hover-background-color: hsl(0deg 0% 96%);\n  --row-selected-background-color: hsl(207deg 76% 92%);\n  --row-selected-hover-background-color: hsl(207deg 76% 88%);\n  --checkbox-color: hsl(207deg 100% 29%);\n  --checkbox-focus-color: hsl(207deg 100% 69%);\n  --checkbox-disabled-border-color: #ccc;\n  --checkbox-disabled-background-color: #ddd;\n  height: 350px;\n  border: 1px solid #e7e7ed;\n  box-sizing: border-box;\n  overflow: auto;\n  /* user-select: none; */\n  background-color: var(--background-color);\n  color: var(--color);\n  border-radius: 0.8rem;\n  font-size: 1.4rem;\n}\n\n.table-module_sn-table__2qRK2 .rdt-header-row {\n  display: grid;\n  grid-template-columns: var(--template-columns);\n  width: calc(var(--row-width) * 1px);\n  background-color: var(--header-background-color);\n  font-weight: bold;\n  z-index: 3;\n  grid-template-rows: var(--header-row-height);\n  height: auto;\n  touch-action: none;\n  min-height: 5rem;\n}\n\n.table-module_sn-table__2qRK2 .rdt-filter-row {\n  grid-template-rows: var(--filter-row-height);\n  height: var(--filter-row-height);\n  line-height: var(--filter-row-height);\n  top: var(--header-row-height);\n}\n\n.table-module_sn-table__2qRK2 .rdt-row {\n  display: grid;\n  grid-template-rows: var(--row-height);\n  grid-template-columns: var(--template-columns);\n  width: calc(var(--row-width) * 1px);\n  height: var(--row-height);\n  line-height: var(--row-height);\n  background-color: var(--background-color);\n}\n\n.table-module_sn-table__2qRK2 .rdt-row[aria-selected='true'] {\n  background-color: var(--row-selected-background-color);\n}\n\n/* .rdt-row:hover {\n  @apply bg-gray-100;\n} */\n\n.table-module_sn-table__2qRK2 .rdt-row[aria-selected='true'] {\n  background-color: var(--row-selected-background-color);\n}\n\n.table-module_sn-table__2qRK2 .rdt-row[aria-selected='true']:hover {\n  background-color: var(--row-selected-hover-background-color);\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell.rdt-cell-resizable {\n  position: -webkit-sticky;\n  position: sticky;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-resizable::after {\n  content: '';\n  cursor: col-resize;\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  width: 2px;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-resizable:hover::after {\n  background-color: #254dda;\n}\n\n.table-module_sn-table__2qRK2 .rdt.table-plus-style .rdt-header-row {\n  background-color: inherit;\n  font-weight: inherit;\n}\n\n.table-module_sn-table__2qRK2 .rdt.table-plus-style .rdt-cell:hover {\n  --bg-opacity: 1;\n  background-color: #F6F7F9;\n  background-color: rgba(246, 247, 249, var(--bg-opacity));\n}\n\n/* .rdg.table-plus-style .rdt-cell:active {\n  @apply bg-gray-200;\n} */\n\n.table-module_sn-table__2qRK2 .rdt-editor-container {\n  width: 100%;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-copied {\n  --bg-opacity: 1;\n  background-color: #EEF1FC;\n  background-color: rgba(238, 241, 252, var(--bg-opacity));\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-drag-handle {\n  cursor: move;\n  position: absolute;\n  right: 0;\n  bottom: 0;\n  width: 8px;\n  height: 8px;\n  background-color: #254dda;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-dragged-over {\n  --bg-opacity: 1;\n  background-color: #EEF1FC;\n  background-color: rgba(238, 241, 252, var(--bg-opacity));\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-frozen {\n  position: -webkit-sticky;\n  position: sticky;\n  z-index: 1;\n}\n\n.table-module_sn-table__2qRK2 .rdt-cell-frozen-last {\n  box-shadow: 2px 0 5px -2px rgba(136, 136, 136, 0.3);\n}\n\n.table-module_sn-table__2qRK2 .table-url-formatter:hover > .table-url-format-icon  {\n  display: flex;\n}\n";
var styles = {
  "sn-table": "table-module_sn-table__2qRK2"
};
styleInject(css_248z);

function getSortComparator(columnType, columnKey) {
  switch (columnType) {
    case 'checkbox':
      return (a, b) => {
        var _a, _b, _c;

        return ((_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === ((_b = b[columnKey]) === null || _b === void 0 ? void 0 : _b[columnType]) ? 0 : ((_c = a[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) ? 1 : -1;
      };

    case 'date':
      return (a, b) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;

        const aDate = ((_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.value) && ((_e = castArray((_d = (_c = a[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.value)) === null || _e === void 0 ? void 0 : _e[0]);
        const bDate = ((_g = (_f = b[columnKey]) === null || _f === void 0 ? void 0 : _f[columnType]) === null || _g === void 0 ? void 0 : _g.value) && ((_k = castArray((_j = (_h = b[columnKey]) === null || _h === void 0 ? void 0 : _h[columnType]) === null || _j === void 0 ? void 0 : _j.value)) === null || _k === void 0 ? void 0 : _k[0]);
        return aDate && bDate ? new Date(aDate).getTime() > new Date(bDate).getTime() ? 1 : -1 : aDate ? 1 : bDate ? -1 : 0;
      };

    case 'multi-tag':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aSelected = (_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.selected;
        const bSelected = (_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.selected;
        return aSelected && bSelected ? aSelected.map(obj => obj.label).join(' ').localeCompare(bSelected.map(obj => obj.label).join(' ')) : aSelected ? 1 : bSelected ? -1 : 0;
      };

    case 'person':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aSelected = (_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.people;
        const bSelected = (_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.people;
        return aSelected && bSelected ? aSelected.map(obj => obj.firstname).join(' ').localeCompare(bSelected.map(obj => obj.firstname).join(' ')) : aSelected ? 1 : bSelected ? -1 : 0;
      };

    case 'single-tag':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aSelected = (_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.selected;
        const bSelected = (_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.selected;
        return aSelected && bSelected ? aSelected.label.localeCompare(bSelected.label) : aSelected ? 1 : bSelected ? -1 : 0;
      };

    case 'text':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aText = (_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.content.map(node => Node.string(node)).join(' ');
        const bText = (_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.content.map(node => Node.string(node)).join(' ');
        return aText && bText ? aText.localeCompare(bText) : aText ? 1 : bText ? -1 : 0;
      };

    case 'url':
    case 'email':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aText = (_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.value;
        const bText = (_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.value;
        return aText && bText ? aText.localeCompare(bText) : aText ? 1 : bText ? -1 : 0;
      };

    case 'number':
      return (a, b) => {
        var _a, _b, _c, _d;

        const aNum = BigInt(((_b = (_a = a[columnKey]) === null || _a === void 0 ? void 0 : _a[columnType]) === null || _b === void 0 ? void 0 : _b.value) || '');
        const bNum = BigInt(((_d = (_c = b[columnKey]) === null || _c === void 0 ? void 0 : _c[columnType]) === null || _d === void 0 ? void 0 : _d.value) || '');
        return aNum && bNum ? aNum >= bNum ? 1 : -1 : aNum ? 1 : bNum ? -1 : 0;
      };

    default:
      throw new Error(`unsupported sortColumn: "${columnType}"`);
  }
}

const {
  useCallback: useCallback$4
} = React;

const getNewColummn = () => {
  return {
    key: v4(),
    type: 'text',
    name: 'Column',
    color: getRandomArrayElement$1(TablePlusColorsKeys)
  };
};

const getNewRow = () => {
  return {
    id: v4()
  };
};

const useTableActions = ({
  columns,
  rows,
  onUpdate
}) => {
  const addColumn = useCallback$4(({
    index,
    direction,
    isNew
  }) => {
    let updatedColumns = null;

    if (isNew) {
      updatedColumns = [// remove `new` column to move from backward compatibility
      ...columns.filter(obj => obj.type !== 'new').slice(0, columns.length), getNewColummn()];
    } else if (typeof index === 'number' && index > -1) {
      switch (direction) {
        case 'left':
          updatedColumns = [...columns.slice(0, index), getNewColummn(), ...columns.slice(index, columns.length)];
          break;

        case 'right':
          updatedColumns = [...columns.slice(0, index + 1), getNewColummn(), ...columns.slice(index + 1, columns.length)];
          break;
      }
    }

    if (updatedColumns) {
      onUpdate({
        column: updatedColumns
      });
    }
  }, [columns, onUpdate]);
  const updateTagOptions = useCallback$4((columnKey, options) => {
    const updatedColumns = cloneDeep(columns);
    const foundColumn = updatedColumns.find(obj => obj.key === columnKey);

    if (foundColumn) {
      const tags = (foundColumn === null || foundColumn === void 0 ? void 0 : foundColumn.tagOptions) || [];
      const newTags = [];
      options.map(tag => {
        if (tags.findIndex(obj => obj.id === tag.id) < 0) {
          newTags.push(tag);
        }
      });

      if (newTags.length > 0) {
        foundColumn.tagOptions = [...tags, ...newTags];
        onUpdate({
          column: updatedColumns
        });
      }
    }
  }, [onUpdate, columns]);
  const deleteColumn = useCallback$4(index => {
    const updatedColumns = columns.filter((obj, _index) => _index !== index);
    onUpdate({
      column: updatedColumns
    });
  }, [columns, onUpdate]);
  const changeColName = useCallback$4(({
    colName,
    colIndex
  }) => {
    const updatedColumns = [...columns];
    updatedColumns[colIndex] = Object.assign(Object.assign({}, updatedColumns[colIndex]), {
      name: colName
    });
    onUpdate({
      column: updatedColumns
    });
  }, [columns, onUpdate]);
  const changeColColor = useCallback$4(({
    colColor,
    colIndex
  }) => {
    const updatedColumns = [...columns];
    updatedColumns[colIndex] = Object.assign(Object.assign({}, updatedColumns[colIndex]), {
      color: colColor
    });
    onUpdate({
      column: updatedColumns
    });
  }, [columns, onUpdate]);
  const changeColType = useCallback$4(({
    colIndex,
    colType
  }) => {
    const updatedColumns = [...columns];
    updatedColumns[colIndex] = Object.assign(Object.assign({}, updatedColumns[colIndex]), {
      type: colType
    });
    onUpdate({
      column: updatedColumns
    });
  }, [columns, onUpdate]);
  const addRow = useCallback$4(({
    rowIndex,
    direction,
    isNew
  }) => {
    let updatedRows = null;

    if (rowIndex !== undefined) {
      switch (direction) {
        case 'above':
          updatedRows = update(rows, {
            $splice: [[rowIndex, 0, getNewRow()]]
          });
          break;

        case 'below':
          updatedRows = update(rows, {
            $splice: [[rowIndex + 1, 0, getNewRow()]]
          });
          break;

        case 'duplicate':
          updatedRows = update(rows, {
            $splice: [[rowIndex + 1, 0, Object.assign(Object.assign({}, rows[rowIndex]), {
              id: v4()
            })]]
          });
          break;

        case 'remove':
          updatedRows = update(rows, {
            $splice: [[rowIndex, 1]]
          });
          break;
      }
    } else if (isNew) {
      updatedRows = [...rows, getNewRow()];
    }

    if (updatedRows) {
      onUpdate({
        row: updatedRows
      });
    }
  }, [rows, onUpdate]);
  const reOrderRows = useCallback$4(({
    fromIndex,
    toIndex
  }) => {
    const newRows = [...rows];
    newRows.splice(toIndex, 0, newRows.splice(fromIndex, 1)[0]);
    onUpdate({
      row: newRows
    });
  }, [rows, onUpdate]);
  const reOrderColumns = useCallback$4(({
    sourceKey,
    targetKey
  }) => {
    const sourceColumnIndex = columns.findIndex(c => c.key === sourceKey);
    const targetColumnIndex = columns.findIndex(c => c.key === targetKey);
    const reorderedColumns = [...columns];
    reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
    onUpdate({
      column: reorderedColumns
    });
  }, [columns, onUpdate]);
  const changeColWidth = useCallback$4(({
    colIndex,
    width
  }) => {
    const updatedColumns = [// remove `new` column to for backward compatibility
    ...columns.filter(obj => obj.type !== 'new').slice(0, columns.length)];

    if (updatedColumns === null || updatedColumns === void 0 ? void 0 : updatedColumns[colIndex - 1]) {
      updatedColumns[colIndex - 1] = Object.assign(Object.assign({}, updatedColumns[colIndex - 1]), {
        width
      });
    }

    onUpdate({
      column: updatedColumns
    });
  }, [columns, onUpdate]);
  return {
    addRow,
    changeColType,
    changeColColor,
    changeColName,
    deleteColumn,
    updateTagOptions,
    addColumn,
    reOrderRows,
    reOrderColumns,
    changeColWidth
  };
};

const getRandomArrayElement$1 = array => array[Math.floor(Math.random() * array.length)];

const {
  useState: useState$5,
  useCallback: useCallback$3,
  useMemo: useMemo$3,
  useRef: useRef$3
} = React;

function rowKeyGetter(row) {
  return row.id;
}

const Table = ({
  columns,
  rows,
  onUpdateTable,
  members,
  isReadOnly = false
}) => {
  const ref = useRef$3(null);
  const [sortColumns, setSortColumns] = useState$5([]);
  const {
    addColumn,
    addRow,
    changeColColor,
    changeColName,
    changeColType,
    deleteColumn,
    updateTagOptions,
    reOrderColumns,
    reOrderRows,
    changeColWidth
  } = useTableActions({
    columns,
    rows,
    onUpdate: onUpdateTable
  });

  const _handleAddColumn = () => {
    addColumn({
      isNew: true
    });
    setTimeout(() => {
      var _a, _b;

      (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.element) === null || _b === void 0 ? void 0 : _b.scrollTo({
        left: ref.current.element.scrollWidth + 3000 || 0,
        behavior: 'smooth',
        top: 0
      });
    }, 500);
  };

  const [selectedRows, setSelectedRows] = useState$5(() => new Set());

  const handleUpdateRows = _rows => {
    onUpdateTable({
      row: _rows
    });
  };

  const handleUpdateTagOptions = useCallback$3(columnKey => options => {
    updateTagOptions(columnKey, options);
  }, [updateTagOptions]);

  const handleOnAddColumn = (index, direction) => {
    addColumn({
      direction,
      index
    });
  };

  const handleOnDeleteColumn = index => {
    deleteColumn(index);
  };

  const handleOnNameChange = (index, name) => {
    changeColName({
      colIndex: index,
      colName: name
    });
  };

  const handleOnChangeColor = (index, color) => {
    changeColColor({
      colColor: color,
      colIndex: index
    });
  };

  const handleOnPropertyChange = (index, value) => {
    changeColType({
      colIndex: index,
      colType: value
    });
  };

  const handleOnSortChange = (index, direction) => {
    if (direction) {
      const colKey = columns[index].key;
      setSortColumns([{
        columnKey: colKey,
        direction
      }]);
    } else {
      setSortColumns([]);
    }
  };

  const Header = useCallback$3(({
    props,
    index
  }) => /*#__PURE__*/React.createElement(DraggableHeaderWrapper, Object.assign({}, props, {
    onColumnsReorder: handleColumnsReorder
  }), /*#__PURE__*/React.createElement(HeaderMenu, Object.assign({
    onAddColumn: handleOnAddColumn.bind(undefined, index),
    onDeleteColumn: handleOnDeleteColumn.bind(undefined, index),
    onNameChange: handleOnNameChange.bind(undefined, index),
    onChangeColor: handleOnChangeColor.bind(undefined, index),
    onPropertyChange: handleOnPropertyChange.bind(undefined, index),
    onSortChange: handleOnSortChange.bind(undefined, index),
    isReadOnly: isReadOnly,
    sortColumns: sortColumns
  }, props))), [handleOnAddColumn, handleOnDeleteColumn, handleOnNameChange, handleOnChangeColor, handleOnPropertyChange, sortColumns, isReadOnly]);
  const getFormatter = useCallback$3(type => {
    const components = {
      checkbox: CheckboxRenderer,
      text: TextFormatter,
      person: PeopleFormatter,
      'single-tag': SingleTagFormatter,
      'multi-tag': MultiTagFormatter,
      date: DateFormatter,
      number: NumberFormatter,
      email: EmailFormatter,
      url: UrlFormatter
    };

    if (components[type]) {
      return components[type];
    }

    return ValueFormatter;
  }, []);
  const getEditor = useCallback$3(({
    tags,
    colKey,
    type
  }) => {
    const renderSingleTag = p => /*#__PURE__*/React.createElement(SingleTagEditor, Object.assign({}, p, {
      tagOptions: tags || [],
      onUpdateTagOptions: handleUpdateTagOptions(colKey)
    }));

    const renderMultiTag = p => /*#__PURE__*/React.createElement(MultiTagEditor, Object.assign({}, p, {
      tagOptions: tags || [],
      onUpdateTagOptions: handleUpdateTagOptions(colKey)
    }));

    const editors = {
      text: TextEditor,
      person: p => /*#__PURE__*/React.createElement(PeopleRenderer, Object.assign({}, p, {
        members: members
      })),
      'single-tag': renderSingleTag,
      'multi-tag': renderMultiTag,
      date: DateEditor,
      number: NumberEditor,
      email: EmailEditor,
      url: UrlEditor
    };

    if (editors[type]) {
      return editors[type];
    }
  }, [members, handleUpdateTagOptions]);
  const withNewCol = useMemo$3(() => {
    if ((columns === null || columns === void 0 ? void 0 : columns.length) < 1) return [];
    return [{
      id: v4(),
      name: 'Serial Number',
      key: v4(),
      type: 'text',
      headerRenderer: () => null,
      width: 20,
      frozen: true,
      // eslint-disable-next-line react/display-name
      formatter: props => /*#__PURE__*/React.createElement(RowNumber, Object.assign({}, props, {
        rows: rows,
        onAddRow: handleAddRow,
        onRowReorder: onRowReorder
      }))
    }, ...columns.filter(obj => obj.type !== 'new') // this is for backward compatibility
    .map((c, index) => {
      return Object.assign(Object.assign({}, c), {
        headerRenderer: function Head(p) {
          return /*#__PURE__*/React.createElement(Header, {
            props: Object.assign(Object.assign({}, p), {
              type: c.type
            }),
            index: index
          });
        },
        resizable: true,
        formatter: getFormatter(c.type),
        editor: getEditor({
          tags: c.tagOptions,
          colKey: c.key,
          type: c.type
        }),
        width: c.width || 300,
        editorOptions: {
          editOnClick: true
        }
      });
    })];
  }, [columns, rows]);
  const sortedRows = useMemo$3(() => {
    if (sortColumns.length === 0) return rows;
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      var _a;

      for (const sort of sortColumns) {
        const colType = (_a = columns.find(obj => obj.key === sort.columnKey)) === null || _a === void 0 ? void 0 : _a.type;

        if (colType) {
          const comparator = getSortComparator(colType, sort.columnKey);
          const compResult = comparator(a, b);

          if (compResult !== 0) {
            return sort.direction === 'ASC' ? compResult : -compResult;
          }
        }
      }

      return 0;
    });
    return sortedRows;
  }, [rows, sortColumns, columns]);

  const handleAddRow = (arg, index) => {
    addRow({
      direction: arg,
      rowIndex: index,
      isNew: index === undefined
    });
  };

  function onRowReorder(fromIndex, toIndex) {
    reOrderRows({
      fromIndex,
      toIndex
    });
  }

  function handleColumnsReorder(sourceKey, targetKey) {
    reOrderColumns({
      sourceKey,
      targetKey
    });
  }

  const debouncedWidthChange = useCallback$3(debounce(async (colIndex, width) => {
    changeColWidth({
      colIndex,
      width
    });
  }, 500), [columns]); // TODO: Refactor the on paste code

  const handleOnPaste = e => {
    var _a, _b, _c;

    if (!e.selectedRange) {
      const sourceColType = (_a = columns.find(column => column.key === e.sourceColumnKey)) === null || _a === void 0 ? void 0 : _a.type;
      const targetColType = (_b = columns.find(column => column.key === e.targetColumnKey)) === null || _b === void 0 ? void 0 : _b.type;

      if (sourceColType && targetColType && sourceColType === targetColType) {
        return Object.assign(Object.assign({}, e.targetRow), {
          [e.targetColumnKey]: e.sourceRow[e.sourceColumnKey]
        });
      }

      return e.targetRow;
    }

    const clonedRows = cloneDeep(rows);
    const newRows = [];
    clonedRows.slice(e.selectedRange.topLeft.rowIdx, e.selectedRange.bottomRight.rowIdx + 1).map(row => {
      var _a;

      const rowData = [];
      e.selectedRange && columns.slice(e.selectedRange.topLeft.idx - 1, (_a = e.selectedRange) === null || _a === void 0 ? void 0 : _a.bottomRight.idx).map(column => {
        const properties = row[column.key];

        if (properties) {
          rowData.push(properties);
        }
      });
      newRows.push(rowData);
    });
    const rowStartIdx = clonedRows.findIndex(obj => obj.id === e.targetRow.id);
    const colStartIdx = columns.findIndex(obj => obj.key === e.targetColumnKey);

    for (let i = 0; i < newRows.length; i++) {
      for (let j = 0; j < newRows[i].length; j++) {
        const colId = (_c = columns[j + colStartIdx]) === null || _c === void 0 ? void 0 : _c.key;

        if (colId && rowStartIdx + i < clonedRows.length) {
          clonedRows[rowStartIdx + i] = Object.assign(Object.assign({}, clonedRows[rowStartIdx + i]), {
            [colId]: Object.assign(Object.assign({}, clonedRows[rowStartIdx + i][colId]), newRows[i][j])
          });
        } else if (rowStartIdx + i === clonedRows.length) {
          clonedRows.push({
            id: v4(),
            [colId]: Object.assign({}, newRows[i][j])
          });
        }
      }
    }

    handleUpdateRows(clonedRows);
  };

  const handleOnCopy = e => {
    var _a, _b;

    const {
      idx,
      rowIdx
    } = e.selectedPosition;
    const clonedRows = cloneDeep(rows);

    if (e.selectedRange && e.selectedPosition) {
      const rowValues = clonedRows.slice(e.selectedRange.topLeft.rowIdx, e.selectedRange.bottomRight.rowIdx + 1);
      let copiedData = ``;

      if (e.selectedRange.startCell) {
        const copiedColumns = columns.slice(e.selectedRange.topLeft.idx - 1, e.selectedRange.bottomRight.idx);
        rowValues.map(row => {
          copiedColumns.map(({
            key
          }) => {
            var _a;

            return copiedData += `${getCopiedValue(row[key], (_a = columns.find(column => column.key === key)) === null || _a === void 0 ? void 0 : _a.type)}\t\t`;
          });
          copiedData += `\n`;
        });
      } else {
        const columnKey = (_a = columns.find(column => column.key === columns[idx - 1].key)) === null || _a === void 0 ? void 0 : _a.key;

        if (columnKey) {
          copiedData = getCopiedValue(clonedRows[rowIdx][columnKey], (_b = columns.find(column => column.key === columns[idx - 1].key)) === null || _b === void 0 ? void 0 : _b.type);
        }
      }

      navigator.clipboard.writeText(copiedData);
    }
  };

  const gridElement = /*#__PURE__*/React.createElement(DataGrid, {
    columns: withNewCol,
    rows: sortedRows,
    rowKeyGetter: rowKeyGetter,
    onRowsChange: handleUpdateRows,
    selectedRows: selectedRows,
    onSelectedRowsChange: setSelectedRows,
    className: "fill-grid rdt-light table-plus-style",
    cellNavigationMode: "CHANGE_ROW",
    style: {
      height: '100%',
      contain: 'content'
    },
    enableVirtualization: false,
    onPaste: handleOnPaste,
    onCopy: handleOnCopy,
    ref: ref,
    rowHeight: 'auto',
    onColumnResize: debouncedWidthChange,
    onSortColumnsChange: setSortColumns,
    onFill: e => {
      return Object.assign(Object.assign({}, e.targetRow), {
        [e.columnKey]: Object.assign(Object.assign({}, e.targetRow[e.columnKey]), e.sourceRow[e.columnKey])
      });
    },
    defaultColumnOptions: {
      sortable: true,
      resizable: true
    }
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cn('flex', styles['sn-table'])
  }, (rows === null || rows === void 0 ? void 0 : rows.length) && (columns === null || columns === void 0 ? void 0 : columns.length) ? gridElement : null, !isReadOnly && /*#__PURE__*/React.createElement("div", {
    className: "text-gray-400 mt-10 ml-5 p-5 hover:text-gray-800",
    contentEditable: false
  }, /*#__PURE__*/React.createElement(IconTextBtn, {
    onClick: _handleAddColumn,
    iconLeft: /*#__PURE__*/React.createElement(AddIcon, null)
  }))), !isReadOnly && /*#__PURE__*/React.createElement("div", {
    className: "text-gray-400 mt-5 ml-5 p-5 hover:text-gray-800",
    contentEditable: false
  }, /*#__PURE__*/React.createElement(IconTextBtn, {
    onClick: () => handleAddRow(),
    iconLeft: /*#__PURE__*/React.createElement(AddIcon, null)
  })));
};

const getRandomArrayElement = array => array[Math.floor(Math.random() * array.length)];

const getCopiedValue = (data, type) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;

  if (data) {
    const components = {
      checkbox: 'Yes',
      text: (_b = (_a = data === null || data === void 0 ? void 0 : data.text) === null || _a === void 0 ? void 0 : _a.content) === null || _b === void 0 ? void 0 : _b.map(obj => Node.string(obj)).join('\t'),
      person: (_d = (_c = data === null || data === void 0 ? void 0 : data.person) === null || _c === void 0 ? void 0 : _c.people) === null || _d === void 0 ? void 0 : _d.map(person => person.firstname).join(','),
      'single-tag': (_f = (_e = data['single-tag']) === null || _e === void 0 ? void 0 : _e.selected) === null || _f === void 0 ? void 0 : _f.label,
      'multi-tag': (_h = (_g = data['multi-tag']) === null || _g === void 0 ? void 0 : _g.selected) === null || _h === void 0 ? void 0 : _h.map(tag => tag.label).join(','),
      date: (_k = (_j = data.date) === null || _j === void 0 ? void 0 : _j.value) === null || _k === void 0 ? void 0 : _k.map(date => format(parseISO(date), 'do MMM yyyy')).join(' → '),
      number: (_l = data.number) === null || _l === void 0 ? void 0 : _l.value,
      email: (_m = data.email) === null || _m === void 0 ? void 0 : _m.value,
      url: (_o = data.url) === null || _o === void 0 ? void 0 : _o.value
    };
    return components[type] || '';
  }

  return '';
};

const {
  useState: useState$4,
  useMemo: useMemo$2,
  useEffect: useEffect$3,
  useRef: useRef$2
} = React;

const MultiTagEditor = ({
  column,
  row,
  onRowChange,
  onClose,
  tagOptions = [],
  onUpdateTagOptions
}) => {
  var _a, _b;

  const readOnly = useReadOnly();
  const selectedItems = useMemo$2(() => {
    var _a, _b;

    return ((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected) || [];
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected]);
  const referenceElement = useRef$2(null);
  const popperElement = useRef$2(null);
  const {
    styles,
    attributes,
    update: popperUpdate
  } = usePopper(referenceElement.current, popperElement.current, {
    placement: 'bottom-start'
  });

  const updateItems = selected => {
    if (!readOnly) {
      const updatedRow = update(row, {
        $merge: {
          [column.key]: {
            [column.type]: {
              selected: selected
            }
          }
        }
      });
      onRowChange(updatedRow);
      onUpdateTagOptions(selected);
      popperUpdate === null || popperUpdate === void 0 ? void 0 : popperUpdate();
    }
  };

  useEffect$3(() => {
    popperUpdate === null || popperUpdate === void 0 ? void 0 : popperUpdate();
  }, [row, column, tagOptions]);
  useEffect$3(() => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            selected: selectedItems
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    onKeyDown: e => e.key === 'Enter' && e.stopPropagation(),
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute -top-3 -left-3"
  }, /*#__PURE__*/React.createElement("button", {
    ref: referenceElement,
    className: "absolute"
  }), /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: popperElement,
    style: Object.assign(Object.assign({}, styles.popper), {
      boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
      width: column.width ? `${column.width}px` : undefined
    }),
    className: "bg-white min-w-200 overflow-y-auto rounded-lg shadow-sm p-6"
  }, attributes.popper), /*#__PURE__*/React.createElement(SelectMultiTag, {
    onBlur: onClose,
    selectedTags: selectedItems,
    onChangeTags: updateItems,
    tags: tagOptions
  })))));
};

const SelectMultiTag = ({
  selectedTags,
  onBlur,
  onChangeTags,
  tags
}) => {
  const [inputValue, setInputValue] = useState$4('');
  const inputRef = useRef$2(null);
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems
  } = useMultipleSelection({
    initialSelectedItems: [],
    selectedItems: selectedTags,
    onStateChange: s => {
      if (s.selectedItems) onChangeTags(s.selectedItems);
    }
  });
  const filteredItems = useMemo$2(() => tags.filter(tag => !selectedItems.find(obj => obj.id === tag.id) && tag.label.toLowerCase().startsWith((inputValue === null || inputValue === void 0 ? void 0 : inputValue.toLowerCase()) || '')), [tags, inputValue, selectedItems]);
  const {
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    isOpen,
    openMenu
  } = useCombobox({
    inputValue,
    items: filteredItems,
    defaultHighlightedIndex: 0,
    onInputValueChange: e => {
      if (e.type === useCombobox.stateChangeTypes.InputKeyDownEnter && e.selectedItem) {
        addSelectedItem(e.selectedItem);
        setInputValue('');
        return;
      }

      setInputValue(e.inputValue || '');
    },
    defaultIsOpen: true,
    onStateChange: ({
      type,
      selectedItem
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setInputValue('');
            addSelectedItem(selectedItem);
          }

          break;
      }
    }
  });
  const isNewLabel = useMemo$2(() => {
    return filteredItems.length === 0 && inputValue && !selectedItems.find(obj => obj.label === inputValue);
  }, [filteredItems, inputValue, selectedItems]);

  const handleAddNewTag = () => {
    if (isNewLabel) {
      addSelectedItem({
        label: inputValue || '',
        id: v4(),
        color: getRandomArrayElement(TablePlusColorsKeys)
      });
      setInputValue('');
    }
  };

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap bg-gray-100 py-10 rounded-md px-8"
  }, selectedItems.map((selectedItem, index) => /*#__PURE__*/React.createElement("span", Object.assign({
    key: `selected-item-${index}`
  }, getSelectedItemProps({
    selectedItem,
    index
  }), {
    className: cn('mb-3 flex items-center text-sm ml-4 px-6 py-4 rounded-sm', TablePlusColors[selectedItem.color])
  }), /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedItem.label), /*#__PURE__*/React.createElement("button", {
    onClick: e => {
      e.stopPropagation();
      e.preventDefault();
      removeSelectedItem(selectedItem);
      setTimeout(() => {
        var _a;

        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, 500);
    },
    className: "ml-5"
  }, "\u2715"))), /*#__PURE__*/React.createElement("div", Object.assign({}, getComboboxProps({
    className: 'w-full'
  })), /*#__PURE__*/React.createElement("input", Object.assign({}, getInputProps(getDropdownProps({
    autoFocus: true,
    ref: inputRef,
    onBlur: onBlur,
    onFocus: () => openMenu(),
    onKeyDown: e => {
      if (e.key === 'Enter') {
        handleAddNewTag();
      }
    }
  })), {
    className: "w-full bg-transparent"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-200 mt-12"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-sm text-gray-500 py-12 mx-14"
  }, "Select an option or create one"), /*#__PURE__*/React.createElement("ul", Object.assign({}, getMenuProps({
    className: 'max-h-500 overflow-auto'
  })), isOpen && filteredItems.length ? filteredItems.map((item, index) => /*#__PURE__*/React.createElement("li", Object.assign({
    key: `${item.id}${index}`
  }, getItemProps({
    item,
    index,
    className: cn('list-none flex items-center py-10 px-12 cursor-pointer', {
      'bg-gray-100': highlightedIndex === index
    }),
    onMouseDown: () => {
      addSelectedItem(item);
      setTimeout(() => {
        var _a;

        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, 500);
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "ml-12"
  }, /*#__PURE__*/React.createElement("span", {
    className: cn('mb-3 flex items-center text-sm ml-4 px-6 py-4 rounded-sm', TablePlusColors[item.color])
  }, item.label)))) : isNewLabel ? /*#__PURE__*/React.createElement("button", {
    className: "flex w-full items-center py-10 px-12 bg-gray-100",
    onClick: handleAddNewTag
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-gray-500 min-w-100"
  }, "+ create new"), /*#__PURE__*/React.createElement("span", {
    className: "px-6 py-4 rounded-sm bg-red-200 ml-12 truncate"
  }, inputValue)) : null));
};

const MultiTagFormatter = ({
  column,
  row
}) => {
  var _a, _b;

  const selectedItems = useMemo$2(() => {
    var _a, _b;

    return ((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected) || [];
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap"
  }, selectedItems.map((selectedItem, index) => /*#__PURE__*/React.createElement("span", {
    key: `selected-item-${index}`,
    className: cn('mb-3 flex items-center text-sm ml-4 px-6 py-4 rounded-sm', TablePlusColors[selectedItem.color])
  }, /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedItem.label))));
};

const {
  useEffect: useEffect$2,
  useState: useState$3,
  useCallback: useCallback$2
} = React;

const NumberEditor = ({
  column,
  row,
  onRowChange,
  onClose
}) => {
  var _a, _b, _c, _d;

  const [numberLoc, setNumberLoc] = useState$3(((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) || '');
  useEffect$2(() => {
    var _a, _b, _c;

    setNumberLoc((_c = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.toString());
  }, [(_d = (_c = row === null || row === void 0 ? void 0 : row[column.key]) === null || _c === void 0 ? void 0 : _c[column.type]) === null || _d === void 0 ? void 0 : _d.value]);
  const updateAndClose = useCallback$2(() => {
    var _a;

    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            value: ((_a = numberLoc === null || numberLoc === void 0 ? void 0 : numberLoc.match(/\d/g)) === null || _a === void 0 ? void 0 : _a.join('')) || ''
          }
        }
      }
    });
    onRowChange(updatedRow, true);
    onClose(true);
  }, [row, column, numberLoc, onClose]);
  const handleOnChange = useCallback$2(e => {
    setNumberLoc(e.target.value);
  }, []);
  const handleOnKeyDown = useCallback$2(e => {
    if (isHotkey('esc', e) || isHotkey('enter', e)) {
      e.preventDefault();
      e.stopPropagation();
      updateAndClose();
    }
  }, [updateAndClose]);
  return /*#__PURE__*/React.createElement("div", {
    className: "w-full"
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: numberLoc,
    className: "w-full bg-inherit text-right",
    onChange: handleOnChange,
    autoFocus: true,
    onKeyDown: handleOnKeyDown,
    onBlur: updateAndClose
  }));
};

const NumberFormatter = ({
  column,
  row
}) => {
  var _a, _b, _c;

  return /*#__PURE__*/React.createElement("div", {
    className: "text-right"
  }, (_c = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '');
};

const {
  useState: useState$2,
  useMemo: useMemo$1,
  useEffect: useEffect$1,
  useRef: useRef$1
} = React;

const PeopleRenderer = ({
  column,
  row,
  onRowChange,
  onClose,
  members
}) => {
  var _a, _b;

  const readOnly = useReadOnly();
  const referenceElement = useRef$1(null);
  const popperElement = useRef$1(null);
  const {
    styles,
    attributes,
    update: popperUpdate
  } = usePopper(referenceElement.current, popperElement.current, {
    placement: 'bottom-start'
  });
  const selectedItemsT = useMemo$1(() => {
    var _a, _b, _c;

    return ((_c = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.people) === null || _c === void 0 ? void 0 : _c.map(obj => ({
      firstname: obj.firstname,
      value: obj.memberId,
      memberId: obj.memberId,
      avatar: obj.avatar
    }))) || [];
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.people]);

  const updateItems = people => {
    if (!readOnly) {
      const updatedRow = update(row, {
        $merge: {
          [column.key]: {
            [column.type]: {
              people: people
            }
          }
        }
      });
      onRowChange(updatedRow);
    }
  };

  useEffect$1(() => {
    popperUpdate === null || popperUpdate === void 0 ? void 0 : popperUpdate();
  }, [row, column]);
  useEffect$1(() => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            people: selectedItemsT
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    onKeyDown: e => e.key === 'Enter' && e.stopPropagation(),
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute -top-3 -left-3"
  }, /*#__PURE__*/React.createElement("button", {
    ref: referenceElement,
    className: "absolute"
  }), /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: popperElement,
    style: Object.assign(Object.assign({}, styles.popper), {
      boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
      width: column.width ? `${column.width}px` : undefined
    }),
    className: "bg-white min-w-200 overflow-y-auto rounded-lg shadow-sm p-6"
  }, attributes.popper), /*#__PURE__*/React.createElement(SelectPeople, {
    users: members,
    onChangeUsers: updateItems,
    selectedUsers: selectedItemsT,
    onBlur: onClose
  })))));
};

const PeopleFormatter = ({
  column,
  row
}) => {
  const selectedItemsT = useMemo$1(() => {
    var _a, _b, _c;

    return ((_c = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.people) === null || _c === void 0 ? void 0 : _c.map(obj => ({
      firstname: obj.firstname,
      value: obj.memberId,
      memberId: obj.memberId,
      avatar: obj.avatar,
      email: obj.email
    }))) || [];
  }, [row, column]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap"
  }, selectedItemsT.map((selectedItem, index) => /*#__PURE__*/React.createElement("span", {
    key: `selected-item-${index}`,
    className: "mb-3 flex items-center px-6 py-3 text-xs mr-4"
  }, /*#__PURE__*/React.createElement(Avatar, {
    src: selectedItem.avatar,
    type: "photo",
    name: selectedItem.firstname
  }), /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedItem.firstname))));
};

const SelectPeople = ({
  users,
  onChangeUsers,
  selectedUsers,
  onBlur
}) => {
  const [inputValue, setInputValue] = useState$2('');
  const inputRef = useRef$1(null);
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems
  } = useMultipleSelection({
    initialSelectedItems: [],
    selectedItems: selectedUsers,
    onStateChange: s => {
      if (s.selectedItems) onChangeUsers(s.selectedItems);
    }
  });
  const filteredItems = useMemo$1(() => users.filter(user => !selectedItems.find(obj => obj.memberId === user.memberId) && (user.firstname.toLowerCase().startsWith(inputValue.toLowerCase()) || user.email.toLowerCase().startsWith(inputValue.toLowerCase()))), [users, inputValue, selectedItems]);
  const {
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    isOpen,
    openMenu
  } = useCombobox({
    inputValue,
    itemToString: i => (i === null || i === void 0 ? void 0 : i.firstname) || '',
    items: filteredItems,
    defaultHighlightedIndex: 0,
    defaultIsOpen: true,
    onInputValueChange: e => {
      if (e.type === useCombobox.stateChangeTypes.InputKeyDownEnter && e.selectedItem) {
        addSelectedItem(e.selectedItem);
        setInputValue('');
        return;
      }

      setInputValue(e.inputValue || '');
    },
    onStateChange: ({
      type,
      selectedItem
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setInputValue('');
            addSelectedItem(selectedItem);
          }

          break;
      }
    }
  });
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap bg-gray-100 py-10 rounded-md px-8"
  }, selectedItems.map((selectedItem, index) => /*#__PURE__*/React.createElement("span", Object.assign({
    key: `selected-item-${index}`
  }, getSelectedItemProps({
    selectedItem,
    index
  }), {
    className: "mb-3 flex items-center px-6 py-3 text-xs mr-4"
  }), /*#__PURE__*/React.createElement(Avatar, {
    src: selectedItem.avatar,
    type: "photo",
    name: selectedItem.firstname
  }), /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedItem.firstname), /*#__PURE__*/React.createElement("button", {
    onClick: e => {
      e.stopPropagation();
      e.preventDefault();
      removeSelectedItem(selectedItem);
      setTimeout(() => {
        var _a;

        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, 500);
    },
    className: "ml-5"
  }, "\u2715"))), /*#__PURE__*/React.createElement("div", Object.assign({}, getComboboxProps({
    className: 'w-full'
  })), /*#__PURE__*/React.createElement("input", Object.assign({}, getInputProps(getDropdownProps({
    autoFocus: true,
    onBlur: onBlur,
    onFocus: () => openMenu(),
    ref: inputRef
  })), {
    className: "w-full bg-transparent"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-200 mt-12"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-sm text-gray-500 py-12 mx-14"
  }, "Select an user"), /*#__PURE__*/React.createElement("ul", Object.assign({}, getMenuProps({
    className: 'max-h-500 overflow-auto'
  })), isOpen && filteredItems.length ? filteredItems.map((item, index) => /*#__PURE__*/React.createElement("li", Object.assign({
    key: `${item.memberId}${index}`
  }, getItemProps({
    item,
    index,
    className: cn('list-none flex items-center py-10 px-12 cursor-pointer', {
      'bg-gray-100': highlightedIndex === index
    }),
    onMouseDown: () => {
      addSelectedItem(item);
      setTimeout(() => {
        var _a;

        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, 500);
    }
  })), /*#__PURE__*/React.createElement(Avatar, {
    src: item.avatar,
    type: "photo",
    name: item.firstname
  }), /*#__PURE__*/React.createElement("div", {
    className: "ml-12"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-sm font-medium"
  }, item.firstname)))) : null));
};

const {
  useState: useState$1,
  Fragment,
  useCallback: useCallback$1
} = React;

const RowNumber = ({
  onAddRow,
  rows,
  row,
  onRowReorder
}) => {
  const rowIdx = rows.findIndex(obj => obj.id === row.id);
  const [isHovered, setIsHovered] = useState$1(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState$1(false);
  const readOnly = useReadOnly();

  const handleOnMouseEnter = () => {
    setIsHovered(true);
  };

  const handleOnMouseLeave = () => {
    setIsHovered(false);
  };

  const handleOpenMenu = value => {
    setIsPopoverOpen(value);
  };

  const [{
    isDragging
  }, drag] = useDrag({
    type: 'ROW_DRAG',
    canDrag: !readOnly,
    item: {
      index: rowIdx
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  const [{
    isOver
  }, drop] = useDrop({
    accept: 'ROW_DRAG',

    drop({
      index
    }) {
      onRowReorder(index, rowIdx);
    },

    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });
  const className = cn('h-full w-full', {
    'opacity-50': isDragging,
    'bg-gray-200 border-b-2 border-primary-500': isOver
  });
  return /*#__PURE__*/React.createElement("div", {
    onMouseEnter: handleOnMouseEnter,
    ref: useCombinedRefs(drag, drop),
    onMouseLeave: handleOnMouseLeave,
    className: className,
    style: {
      cursor: 'grab'
    }
  }, /*#__PURE__*/React.createElement(Popover, {
    onOpen: handleOpenMenu.bind(undefined, true),
    isOpen: isPopoverOpen,
    onClose: handleOpenMenu.bind(undefined, false),
    placement: "left-start"
  }, ({
    isOpen
  }) => /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(PopoverTrigger, null, /*#__PURE__*/React.createElement("button", {
    onClick: () => setIsPopoverOpen(!isPopoverOpen),
    className: cn('cursor-pointer text-gray-999 p-5', {
      'opacity-0': !isHovered
    })
  }, /*#__PURE__*/React.createElement(MoreIcon, {
    wrapperClass: "transform rotate-90"
  }))), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(PopoverContent, {
    className: "bg-white shadow-lg min-w-144 rounded-md max-h-304 overflow-auto"
  }, !readOnly && isOpen ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(Item, {
    label: "Insert above",
    leftIcon: ArrowUpTwoIcon,
    onClick: () => onAddRow('above', rowIdx)
  }), /*#__PURE__*/React.createElement(Item, {
    label: "Insert below",
    leftIcon: ArrowDownTwoIcon,
    onClick: () => onAddRow('below', rowIdx)
  }), /*#__PURE__*/React.createElement(Item, {
    label: "Duplicate",
    leftIcon: ClipboardIcon,
    onClick: () => onAddRow('duplicate', rowIdx)
  }), /*#__PURE__*/React.createElement(Item, {
    label: "Delete",
    leftIcon: DeleteIcon,
    onClick: () => onAddRow('remove', rowIdx)
  }))) : null)))), /*#__PURE__*/React.createElement("span", {
    className: cn('text-gray-400 text-sm', {
      hidden: isHovered
    })
  }, rowIdx + 1));
};

function useCombinedRefs(...refs) {
  return useCallback$1(handle => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(handle);
      } else if (ref !== null) {
        // @ts-expect-error: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31065
        ref.current = handle;
      }
    }
  }, refs);
}

const {
  useState,
  useMemo,
  useRef,
  useEffect
} = React;

const SingleTagEditor = ({
  column,
  row,
  onRowChange,
  onClose,
  tagOptions,
  onUpdateTagOptions
}) => {
  var _a, _b;

  const readOnly = useReadOnly();
  const referenceElement = useRef(null);
  const popperElement = useRef(null);
  const {
    styles,
    attributes
  } = usePopper(referenceElement.current, popperElement.current, {
    placement: 'bottom-start'
  });
  const selectedItem = useMemo(() => {
    var _a, _b;

    return (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected;
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected]);

  const updateItems = selected => {
    if (!readOnly) {
      const updatedRow = update(row, {
        $merge: {
          [column.key]: {
            [column.type]: {
              selected: selected
            }
          }
        }
      });

      if (selected) {
        onUpdateTagOptions([selected]);
      }

      onRowChange(updatedRow, !!selected);
    }
  };

  useEffect(() => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            selected: selectedItem
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    onKeyDown: e => e.key === 'Enter' && e.stopPropagation(),
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute -top-3 -left-3"
  }, /*#__PURE__*/React.createElement("button", {
    ref: referenceElement,
    className: "absolute"
  }), /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: popperElement,
    style: Object.assign(Object.assign({}, styles.popper), {
      boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
      width: column.width ? `${column.width}px` : undefined
    }),
    className: "bg-white min-w-200 overflow-y-auto rounded-lg shadow-sm p-6"
  }, attributes.popper), /*#__PURE__*/React.createElement(SelectSingleTag, {
    onBlur: onClose,
    selectedTag: selectedItem,
    onChangeTag: updateItems,
    tags: tagOptions
  })))));
};

const SelectSingleTag = ({
  selectedTag,
  onBlur,
  onChangeTag,
  tags
}) => {
  const [inputValue, setInputValue] = useState('');
  const filteredItems = useMemo(() => tags.filter(tag => (selectedTag === null || selectedTag === void 0 ? void 0 : selectedTag.id) !== tag.id && tag.label.toLowerCase().startsWith(inputValue.toLowerCase())), [tags, inputValue, selectedTag]);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    selectItem
  } = useCombobox({
    items: filteredItems,
    selectedItem: selectedTag,
    inputValue,
    onInputValueChange: e => {
      setInputValue(e.inputValue || '');
    },
    onSelectedItemChange: changes => {
      changes.selectedItem && onChangeTag(changes.selectedItem);
    },
    defaultHighlightedIndex: 0,
    getItemId: index => {
      var _a;

      return ((_a = tags === null || tags === void 0 ? void 0 : tags[index]) === null || _a === void 0 ? void 0 : _a.id) || '';
    },
    itemToString: () => '',
    defaultIsOpen: true
  });
  const isNewLabel = useMemo(() => {
    return filteredItems.length === 0 && inputValue && (selectedTag === null || selectedTag === void 0 ? void 0 : selectedTag.label) !== inputValue;
  }, [filteredItems, inputValue, selectedTag]);

  const handleAddNewTag = () => {
    if (isNewLabel) {
      onChangeTag({
        label: inputValue,
        id: v4(),
        color: getRandomArrayElement(TablePlusColorsKeys)
      });
      setInputValue('');
    }
  };

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap bg-gray-100 py-10 rounded-md px-8"
  }, selectedTag ? /*#__PURE__*/React.createElement("span", {
    className: cn('px-6 py-4 mb-3 ml-4 text-sm flex items-center rounded-sm text-black', TablePlusColors[selectedTag.color])
  }, /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedTag.label), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      onChangeTag(null);
    },
    className: "ml-5"
  }, "\u2715")) : null, /*#__PURE__*/React.createElement("div", Object.assign({}, getComboboxProps({
    className: 'w-full'
  })), /*#__PURE__*/React.createElement("input", Object.assign({}, getInputProps({
    onFocus: openMenu,
    autoFocus: true,
    onKeyDown: e => {
      if (e.key === 'Backspace' && !inputValue) {
        onChangeTag(null);
      } else if (e.key === 'Enter') {
        handleAddNewTag();
      }
    },
    className: 'w-full bg-transparent',
    onBlur: onBlur
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-gray-200 mt-12"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-sm text-gray-500 py-12 mx-14"
  }, "Select an option or create one"), /*#__PURE__*/React.createElement("ul", Object.assign({}, getMenuProps({
    className: 'max-h-500 overflow-auto'
  })), isOpen && filteredItems.length ? filteredItems.map((item, index) => /*#__PURE__*/React.createElement("li", Object.assign({
    key: `${item.id}${index}`
  }, getItemProps({
    item,
    index,
    className: cn('list-none flex items-center py-10 px-12 cursor-pointer', {
      'bg-gray-100': highlightedIndex === index
    }),
    onMouseDown: () => {
      selectItem(item);
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "ml-12"
  }, /*#__PURE__*/React.createElement("span", {
    className: cn('mb-3 flex items-center px-6 py-4 text-sm rounded-sm text-black', TablePlusColors[item.color])
  }, item.label)))) : isNewLabel ? /*#__PURE__*/React.createElement("button", {
    className: "flex w-full items-center py-10 px-12 bg-gray-100",
    onClick: handleAddNewTag
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-gray-500"
  }, "+ create new"), /*#__PURE__*/React.createElement("span", {
    className: "px-6 py-4 rounded-sm bg-red-200 ml-12 truncate"
  }, inputValue)) : null));
};

const SingleTagFormatter = ({
  column,
  row
}) => {
  var _a, _b;

  const selectedItem = useMemo(() => {
    var _a, _b;

    return (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected;
  }, [(_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.selected]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap"
  }, selectedItem ? /*#__PURE__*/React.createElement("span", {
    className: cn('px-6 mb-3 py-4 flex items-center text-sm rounded-sm text-black', TablePlusColors[selectedItem.color])
  }, /*#__PURE__*/React.createElement("span", {
    className: "ml-4"
  }, selectedItem.label)) : null);
};

const TextEditor = ({
  row,
  column,
  onRowChange,
  onClose
}) => {
  var _a, _b;

  const readOnly = useReadOnly();

  const handleOnChange = value => {
    const updatedRow = update(row, {
      $merge: {
        [column === null || column === void 0 ? void 0 : column.key]: {
          [column === null || column === void 0 ? void 0 : column.type]: {
            content: [{
              children: [{
                text: value
              }],
              type: ELEMENT_DEFAULT
            }]
          }
        }
      }
    });
    onRowChange(updatedRow);
  };

  return /*#__PURE__*/React.createElement("div", {
    onKeyDown: e => {
      if (isHotkey('shift+enter', e)) {
        e.stopPropagation();
      }
    }
  }, /*#__PURE__*/React.createElement(TextareaAutosize, {
    onChange: e => handleOnChange(e.target.value),
    onFocus: e => {
      e.target.setSelectionRange(e.target.value.length, e.target.value.length);
    },
    autoFocus: true,
    disabled: readOnly,
    className: "bg-transparent w-full resize-none",
    onBlur: () => onClose(true),
    value: getFormattedValue((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.content)
  }));
};

const TextFormatter = ({
  row,
  column
}) => {
  var _a, _b;

  return /*#__PURE__*/React.createElement(React.Fragment, null, getFormattedValue((_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.content));
}; // backward compatibility
// function getInitialValue(value) {
//   if (Array.isArray(value)) {
//     return value;
//   }
//   if (typeof value === 'string') {
//     return [{ children: [{ text: value }] }];
//   }
//   return [{ children: [{ text: '' }] }];
// }


function getFormattedValue(value) {
  if (typeof value === 'string') {
    return value;
  } else if (Array.isArray(value)) {
    return value.map(obj => Node.string(obj)).join('\n');
  }

  return '';
}

const {
  useCallback
} = React;

const UrlEditor = ({
  column,
  row,
  onRowChange,
  onClose
}) => {
  var _a, _b;

  const updateAndClose = useCallback(() => {
    onClose(true);
  }, [onClose]);
  const handleOnChange = useCallback(e => {
    const updatedRow = update(row, {
      $merge: {
        [column.key]: {
          [column.type]: {
            value: e.target.value
          }
        }
      }
    });
    onRowChange(updatedRow);
  }, [onRowChange, row, column]);
  const handleOnKeyDown = useCallback(e => {
    if (isHotkey('esc', e) || isHotkey('enter', e)) {
      updateAndClose();
    }
  }, [updateAndClose]);
  return /*#__PURE__*/React.createElement("div", {
    className: "w-full"
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value,
    className: "w-full bg-inherit",
    autoFocus: true,
    onChange: handleOnChange,
    onKeyDown: handleOnKeyDown,
    onBlur: updateAndClose
  }));
};

const UrlFormatter = ({
  column,
  row
}) => {
  var _a, _b, _c, _d, _e, _f, _g;

  const unFormattedUrl = (_b = (_a = row === null || row === void 0 ? void 0 : row[column.key]) === null || _a === void 0 ? void 0 : _a[column.type]) === null || _b === void 0 ? void 0 : _b.value;
  const link = unFormattedUrl ? (unFormattedUrl === null || unFormattedUrl === void 0 ? void 0 : unFormattedUrl.indexOf('://')) === -1 ? 'http://' + unFormattedUrl : unFormattedUrl : '';
  return /*#__PURE__*/React.createElement("div", {
    className: "w-full h-full flex justify-between relative table-url-formatter"
  }, /*#__PURE__*/React.createElement("span", {
    className: "break-all"
  }, (_e = (_d = (_c = row === null || row === void 0 ? void 0 : row[column.key]) === null || _c === void 0 ? void 0 : _c[column.type]) === null || _d === void 0 ? void 0 : _d.value) !== null && _e !== void 0 ? _e : ''), ((_g = (_f = row === null || row === void 0 ? void 0 : row[column.key]) === null || _f === void 0 ? void 0 : _f[column.type]) === null || _g === void 0 ? void 0 : _g.value) && /*#__PURE__*/React.createElement(Tippy, {
    content: "Open Link",
    className: "w-auto h-auto bg-gray-999"
  }, /*#__PURE__*/React.createElement("a", {
    href: link,
    target: "_blank",
    className: "p-6 bg-white underline shadow-sm text-lg hidden rounded-md text-gray-400 absolute top-2 right-2 table-url-format-icon",
    rel: "noreferrer"
  }, /*#__PURE__*/React.createElement(LinkIcon, null))));
};

export { CheckboxRenderer, DateEditor, DateFormatter, DraggableHeaderWrapper, DraggableRowRenderer, EmailEditor, EmailFormatter, MultiTagEditor, MultiTagFormatter, NumberEditor, NumberFormatter, PeopleFormatter, PeopleRenderer, RowNumber, SelectMultiTag, SelectSingleTag, SingleTagEditor, SingleTagFormatter, Table, TablePlusBorderColors, TablePlusColors, TablePlusColorsKeys, TextEditor, TextFormatter, UrlEditor, UrlFormatter, getRandomArrayElement, useCombinedRefs$1 as useCombinedRefs };
//# sourceMappingURL=index.esm.js.map
